import * as z from "zod";

export type PostsFilters = {
  level?: LevelName;
  workshop?: WorkshopName;
};

const postSchema = z.object({
  id: z.number(),
  link: z.string(),
  title: z.object({
    rendered: z.string(),
  }),
  acf: z.object({
    level: levelNameSchema,
    workshop: workshopNameSchema,
  }),
  featured_image: z.object({
    src: z.string(),
    alt: z.string(),
    object_position: z.string(),
  }),
});
export type Post = z.infer<typeof postSchema>;

export function usePosts(options: PostsFilters = {}) {
  const filters = reactive({
    level: options.level,
    workshop: options.workshop,
  });

  const posts = ref<Post[]>([]);
  const pending = ref(false);

  async function fetchPosts(filters: PostsFilters) {
    pending.value = true;
    try {
      const query = new URLSearchParams({ lang: site.lang, per_page: "100" });
      if (filters.level) {
        query.append("level", filters.level);
      }
      if (filters.workshop) {
        query.append("workshop", filters.workshop);
      }
      const response = await fetch(`/wp-json/wp/v2/posts?${query.toString()}`);
      if (!response.ok) {
        throw new Error("Failed to fetch posts");
      }
      const json = await response.json();
      return Object.values(json).map((post) => {
        const { success, data } = postSchema.safeParse(post);
        if (!success) return null;
        return data;
      }).filter((post): post is Post => post !== null);
    }
    catch (error) {
      console.error("Error fetching posts", error);
      return [];
    }
    finally {
      pending.value = false;
    }
  }

  watch(filters, async (newFilters) => {
    const newPosts = await fetchPosts(newFilters);
    if (newPosts) {
      posts.value = newPosts;
    }
  }, {
    immediate: true,
  });

  return { filters, posts, pending };
}
